import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ArrowRight, Plus, Minus } from 'lucide-react';
import { OFFERINGS, CASE_STUDIES } from '../mock';
import ClosingCTA from '../components/ClosingCTA';
import ClientLogo from '../components/ClientLogo';
import ProductMockup from '../components/ProductMockup';
import SEO from '../components/SEO';
import PulseCTA from '../components/PulseCTA';

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } };

const faqPerService = {
  'ai-strategy-and-advisory': [
    ['What if the answer is that AI will not help here?', 'Then that is what the assessment will say, and we will show you what would need to be true for the answer to change. We would rather leave a good relationship on the table than a bad engagement in production.'],
    ['How long does the assessment take?', 'Four to six weeks, depending on estate complexity and stakeholder availability. We do not need a data room; we need access to the systems and time with the people who run them.'],
    ['Do you deliver against the recommendation, or hand it off?', 'Both are possible, and we will state which is the honest choice for your situation. Most clients bring us back to deliver the first increment we scoped.'],
  ],
  'ai-native-engineering': [
    ['How do we know an agent has not touched something it should not have?', 'The Graph Execution Ledger records what the agent proposed and what it actually did. Two different logs, linked. The gap between them is the explainability artefact.'],
    ['What does the two-person rule really mean in practice?', 'One person reviews the plan. A different person approves the release. Nothing reaches production on one signature, or on an agent’s authority. Ever.'],
    ['Can this work on our estate today?', 'It depends on test coverage, environment parity and observability. If those are not in place, the honest path is Platform Modernisation first.'],
  ],
  'platform-and-commerce-modernisation': [
    ['Why not just start with agents on the existing estate?', 'Because agents cannot work safely against an estate with no test coverage or environment parity. The honest sequencing is to modernise first, then accelerate.'],
    ['Do you do big-bang cutovers?', 'No. Migration proceeds in reviewed increments, each with reversal criteria. If an increment does not clear its gate, it does not ship.'],
    ['How long does modernisation take?', 'Twelve to eighteen months on a large multi-brand estate, in visible increments the business can plan around.'],
  ],
  'run-and-optimise': [
    ['Does an agent ever touch production data during triage?', 'No. Triage runs against shadow data with PII stripped. The human reviews the proposed fix and applies it.'],
    ['What is your response commitment on production incidents?', 'Contracted per engagement. Under agent-assisted triage, initial classification and context assembly is typically inside 5 minutes.'],
    ['How do you report outcomes to us?', 'Monthly, from the execution ledger. Number of tickets, resolution time, agent versus human effort, and any actions that hit tier 3 or tier 4 gates.'],
  ],
};

// Anonymised programme snapshots per service — linking to full case studies
// where the client identity IS surfaced (the case-study pages are exempt).
const programmeProof = {
  'ai-strategy-and-advisory':          { slug: 'sephora', mockup: 'beauty' },
  'ai-native-engineering':             { slug: 'fenix',   mockup: 'outdoor' },
  'platform-and-commerce-modernisation': { slug: 'magrabi', mockup: 'optical' },
  'run-and-optimise':                  { slug: 'al-futtaim', mockup: 'superapp' },
};

// Per-service accent so each Service page inherits a distinct visual key while
// staying on the DigitalWorks palette.
const serviceAccent = {
  'ai-strategy-and-advisory':          '#22A94F', // brand green
  'ai-native-engineering':             '#5B4FCF', // governance indigo
  'platform-and-commerce-modernisation': '#C4633F', // terracotta
  'run-and-optimise':                  '#178838', // deep green
};

export default function ServicePage() {
  const { slug } = useParams();
  const offering = OFFERINGS.find((o) => o.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);
  if (!offering) return <Navigate to="/what-we-do" replace />;
  const proofMeta = programmeProof[slug] || programmeProof['ai-strategy-and-advisory'];
  const proofCase = CASE_STUDIES.find((c) => c.slug === proofMeta.slug);
  const faqs = faqPerService[slug] || [];
  const accent = serviceAccent[slug] || '#22A94F';

  return (
    <div data-testid={`service-${slug}`}>
      <SEO
        title={`${offering.title}`}
        description={offering.description || offering.blurb || `${offering.title} at DigitalWorks — advisory and engineering delivered under NeuralWorks.`}
        canonical={`https://digitalworks.co/what-we-do/${slug}`}
      />
      {/* HERO — accent-tinted */}
      <section className="pt-16 pb-12 relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
          style={{ background: `radial-gradient(closest-side, ${accent}18, transparent)` }}
        />
        <div className="dwx relative">
          <Link to="/what-we-do" className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
            <ArrowLeft size={14} /> What we do
          </Link>
          <div className="mt-8 mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>{offering.title}</div>
          <h1 className="serif-tight text-[48px] md:text-[80px] leading-[0.96] mt-4 max-w-4xl">{offering.h1}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <PulseCTA to="/contact" size="sm" variant="green" testId={`service-${slug}-book-session`}>
              Book a working session
            </PulseCTA>
            <Link to="/tools/ai-readiness-diagnostic" className="text-[13px] font-medium underline underline-offset-4" style={{ color: accent }}>
              Or take the AI Readiness Diagnostic first
            </Link>
          </div>
        </div>
      </section>

      {/* PROBLEM + DELIVERABLES */}
      <section className="py-14" style={{ background: `linear-gradient(180deg, ${accent}09 0%, #F4F2EC 100%)` }}>
        <div className="dwx grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6">
            <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>The problem</div>
            <p className="mt-4 text-[17px] leading-relaxed text-[#1A1A1A]/85">{offering.blurb}</p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>What we deliver</div>
            <ul className="mt-4 space-y-3">
              {offering.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-[#1A1A1A]/85">
                  <Check size={18} className="mt-0.5" style={{ color: accent }} /> {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROOF — real case + mockup, accented card */}
      <section className="py-16 bg-[#FBFAF6]">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>Proof</div>
          <h2 className="serif-tight text-[42px] md:text-[54px] leading-[0.98] mt-3">Where this has run.</h2>

          <motion.div
            {...fadeUp}
            className="mt-10 grid grid-cols-12 gap-6 rounded-[24px] overflow-hidden lift"
            style={{ border: `1px solid ${accent}22` }}
          >
            {/* Mockup half */}
            <Link to={`/work/${proofMeta.slug}`} className="col-span-12 md:col-span-6 h-64 md:h-auto bg-[#FBFAF6] border-r" style={{ borderColor: `${accent}18` }}>
              <ProductMockup variant={proofMeta.mockup} kicker={`${(proofCase?.tag || '').toUpperCase()}`} />
            </Link>
            {/* Detail half */}
            <Link to={`/work/${proofMeta.slug}`} className="col-span-12 md:col-span-6 p-8 md:p-10 bg-[#FBFAF6] group">
              <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>{proofCase?.tag}</div>
              <div className="mt-5 h-14 flex items-center">
                {proofCase && <ClientLogo logo={proofCase.logo} name={proofCase.client.split(' · ')[0]} size="md" />}
              </div>
              <div className="text-[12px] mono uppercase tracking-widest text-[#1A1A1A]/55 mt-2">{proofCase?.markets}</div>
              <p className="mt-6 text-[15px] leading-relaxed text-[#1A1A1A]/80">{proofCase?.problem}</p>
              <div className="serif-tight text-[22px] mt-6" style={{ color: accent }}>{proofCase?.outcome}</div>
              <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold group-hover:translate-x-0.5 transition-transform" style={{ color: accent }}>
                Read the full case study <ArrowRight size={14} />
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: accent }}>FAQ</div>
          <h2 className="serif-tight text-[42px] md:text-[54px] leading-[0.98] mt-3 max-w-3xl">The questions we actually get asked.</h2>
          <div className="mt-10 divide-y divide-[#1A1A1A]/10 border-y border-[#1A1A1A]/10">
            {faqs.map(([q, a], i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="py-6" data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${i}`}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full cursor-pointer flex justify-between items-center text-left text-[18px] font-medium gap-6"
                  >
                    <span>{q}</span>
                    <span className="mono text-[12px] text-[#1A1A1A]/50 shrink-0" aria-hidden="true">
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  <motion.div
                    id={`faq-panel-${i}`}
                    role="region"
                    initial={false}
                    animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p className="mt-4 text-[15px] leading-relaxed text-[#1A1A1A]/75 max-w-3xl">{a}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
